/**
 * ConflictResolutionModal.js
 * 
 * Shown when a save returns 409 (conflict). Displays a side-by-side
 * comparison of conflicting fields and lets the user choose which
 * version to keep per field, or accept all mine / all theirs.
 * 
 * Drop into: /root/legalflow/frontend/src/components/record/ConflictResolutionModal.js
 * 
 * Usage:
 *   <ConflictResolutionModal
 *     conflict={conflict}           // from API 409 response
 *     fieldDefinitions={fields}     // for labels
 *     onResolve={(resolvedData) => { ... save again with resolved data ... }}
 *     onCancel={() => setConflict(null)}
 *   />
 */
import React, { useState, useMemo } from 'react';
import { AlertTriangle, Check, X, ArrowRight } from 'lucide-react';

export default function ConflictResolutionModal({
  conflict,
  fieldDefinitions = [],
  onResolve,
  onCancel,
}) {
  // For each conflicting field, track which version the user chose
  const [choices, setChoices] = useState(() => {
    const init = {};
    (conflict?.conflicts || []).forEach(c => {
      init[c.fieldName] = null; // null = not yet chosen
    });
    return init;
  });

  const conflicts = conflict?.conflicts || [];
  const fieldMap = useMemo(() => {
    const map = {};
    fieldDefinitions.forEach(f => {
      map[f.name] = f;
    });
    return map;
  }, [fieldDefinitions]);

  const allChosen = Object.values(choices).every(v => v !== null);

  function choose(fieldName, version) {
    setChoices(prev => ({ ...prev, [fieldName]: version }));
  }

  function chooseAll(version) {
    const updated = {};
    conflicts.forEach(c => { updated[c.fieldName] = version; });
    setChoices(updated);
  }

  function handleResolve() {
    const resolved = {};
    conflicts.forEach(c => {
      resolved[c.fieldName] = choices[c.fieldName] === 'mine'
        ? c.clientValue
        : c.serverValue;
    });
    onResolve(resolved);
  }

  if (!conflict) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b bg-amber-50">
          <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle size={18} className="text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Edit Conflict Detected</h3>
            <p className="text-sm text-gray-600">
              Another user saved changes while you were editing.
              Choose which version to keep for each field.
            </p>
          </div>
        </div>

        {/* Conflict list */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Bulk actions */}
          <div className="flex items-center justify-between pb-3 border-b">
            <span className="text-sm font-medium text-gray-700">
              {conflicts.length} conflicting {conflicts.length === 1 ? 'field' : 'fields'}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => chooseAll('mine')}
                className="text-xs px-3 py-1.5 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors"
              >
                Keep all mine
              </button>
              <button
                onClick={() => chooseAll('theirs')}
                className="text-xs px-3 py-1.5 rounded border border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
              >
                Keep all theirs
              </button>
            </div>
          </div>

          {/* Per-field conflicts */}
          {conflicts.map((c) => {
            const fieldDef = fieldMap[c.fieldName];
            const label = fieldDef?.label || c.fieldName;
            const selected = choices[c.fieldName];

            return (
              <div key={c.fieldName} className="rounded-lg border overflow-hidden">
                {/* Field name header */}
                <div className="px-4 py-2 bg-gray-50 border-b">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  {fieldDef?.field_type && (
                    <span className="ml-2 text-xs text-gray-400 uppercase">{fieldDef.field_type}</span>
                  )}
                </div>

                {/* Side by side comparison */}
                <div className="grid grid-cols-2 divide-x">
                  {/* My version */}
                  <button
                    onClick={() => choose(c.fieldName, 'mine')}
                    className={`p-3 text-left transition-colors ${
                      selected === 'mine'
                        ? 'bg-blue-50 ring-2 ring-inset ring-blue-400'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-blue-600">Your version</span>
                      {selected === 'mine' && <Check size={14} className="text-blue-600" />}
                    </div>
                    <div className="text-sm text-gray-800 break-words">
                      {formatValue(c.clientValue)}
                    </div>
                  </button>

                  {/* Their version */}
                  <button
                    onClick={() => choose(c.fieldName, 'theirs')}
                    className={`p-3 text-left transition-colors ${
                      selected === 'theirs'
                        ? 'bg-purple-50 ring-2 ring-inset ring-purple-400'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-purple-600">Their version</span>
                      {selected === 'theirs' && <Check size={14} className="text-purple-600" />}
                    </div>
                    <div className="text-sm text-gray-800 break-words">
                      {formatValue(c.serverValue)}
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            <X size={14} />
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={!allChosen}
            className={`flex items-center gap-1.5 px-5 py-2 text-sm font-medium rounded-lg transition-colors ${
              allChosen
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ArrowRight size={14} />
            Save resolved version
          </button>
        </div>
      </div>
    </div>
  );
}

function formatValue(value) {
  if (value === null || value === undefined) return <span className="text-gray-400 italic">empty</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return <pre className="text-xs bg-gray-100 rounded p-1 mt-1 overflow-x-auto">{JSON.stringify(value, null, 2)}</pre>;
    } catch {
      return String(value);
    }
  }
  const str = String(value);
  if (str.length > 200) return str.substring(0, 200) + '…';
  return str;
}
